import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Eye,
  CreditCard,
  Users,
  Database,
  FileText,
  Search,
  Download,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Shield,
  Activity,
  MoreHorizontal,
  Settings,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatDistanceToNow } from 'date-fns'

// Mock data for demonstration
const mockTransactionAudits = [
  {
    id: 1,
    transaction_id: 'TXN-123456',
    action: 'Transaction Created',
    user: 'merchant@example.com',
    old_value: null,
    new_value: '{"amount": "100.00", "currency": "USD", "status": "pending"}',
    ip_address: '192.168.1.100',
    timestamp: new Date().toISOString(),
    status: 'success',
  },
  {
    id: 2,
    transaction_id: 'TXN-123456',
    action: 'Status Updated',
    user: 'system@example.com',
    old_value: 'pending',
    new_value: 'completed',
    ip_address: '192.168.1.101',
    timestamp: new Date(Date.now() - 3600000).toISOString(),
    status: 'success',
  },
  {
    id: 3,
    transaction_id: 'TXN-789012',
    action: 'Transaction Failed',
    user: 'system@example.com',
    old_value: 'processing',
    new_value: 'failed',
    ip_address: '192.168.1.102',
    timestamp: new Date(Date.now() - 7200000).toISOString(),
    status: 'failed',
  },
]

const mockUserAudits = [
  {
    id: 1,
    user_id: 123,
    action: 'User Created',
    user: 'admin@example.com',
    old_value: null,
    new_value: '{"email": "newuser@example.com", "role": "merchant"}',
    ip_address: '192.168.1.100',
    timestamp: new Date().toISOString(),
    status: 'success',
  },
  {
    id: 2,
    user_id: 123,
    action: 'Role Updated',
    user: 'admin@example.com',
    old_value: 'merchant',
    new_value: 'admin',
    ip_address: '192.168.1.101',
    timestamp: new Date(Date.now() - 1800000).toISOString(),
    status: 'success',
  },
  {
    id: 3,
    user_id: 456,
    action: 'Login Attempt',
    user: 'user@example.com',
    old_value: null,
    new_value: '{"ip": "192.168.1.103", "success": false}',
    ip_address: '192.168.1.103',
    timestamp: new Date(Date.now() - 3600000).toISOString(),
    status: 'failed',
  },
]

const mockSystemAudits = [
  {
    id: 1,
    action: 'System Configuration Updated',
    user: 'admin@example.com',
    old_value: '{"maintenance_mode": false}',
    new_value: '{"maintenance_mode": true}',
    ip_address: '192.168.1.100',
    timestamp: new Date().toISOString(),
    status: 'success',
  },
  {
    id: 2,
    action: 'API Rate Limit Changed',
    user: 'admin@example.com',
    old_value: '1000',
    new_value: '2000',
    ip_address: '192.168.1.101',
    timestamp: new Date(Date.now() - 7200000).toISOString(),
    status: 'success',
  },
  {
    id: 3,
    action: 'Database Backup Failed',
    user: 'system@example.com',
    old_value: null,
    new_value: '{"error": "Disk space insufficient"}',
    ip_address: '192.168.1.102',
    timestamp: new Date(Date.now() - 10800000).toISOString(),
    status: 'failed',
  },
]

export default function Audit() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'transactions')
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState({
    start: searchParams.get('start_date') || '',
    end: searchParams.get('end_date') || '',
  })

  const tabs = [
    { id: 'transactions', label: 'Transaction Audit', icon: CreditCard },
    { id: 'users', label: 'User Audit', icon: Users },
    { id: 'system', label: 'System Audit', icon: Database },
    { id: 'reports', label: 'Reports', icon: FileText },
  ]

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'success':
        return { color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle }
      case 'failed':
        return { color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle }
      case 'warning':
        return { color: 'text-yellow-600 bg-yellow-50 border-yellow-200', icon: AlertCircle }
      default:
        return { color: 'text-gray-600 bg-gray-50 border-gray-200', icon: Clock }
    }
  }

  const getAuditData = () => {
    switch (activeTab) {
      case 'transactions':
        return mockTransactionAudits
      case 'users':
        return mockUserAudits
      case 'system':
        return mockSystemAudits
      default:
        return []
    }
  }

  const refreshData = async () => {
    setLoading(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    setLoading(false)
  }

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    const params = new URLSearchParams(searchParams)
    params.set('tab', tabId)
    setSearchParams(params)
  }

  const handleDateRangeChange = (key: string, value: string) => {
    const newRange = { ...dateRange, [key]: value }
    setDateRange(newRange)

    const params = new URLSearchParams(searchParams)
    if (newRange.start) params.set('start_date', newRange.start)
    else params.delete('start_date')
    if (newRange.end) params.set('end_date', newRange.end)
    else params.delete('end_date')
    setSearchParams(params)
  }

  const filteredData = getAuditData().filter(
    item =>
      item.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.user.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getAuditIcon = (action: string) => {
    if (action.includes('Transaction')) return CreditCard
    if (action.includes('User')) return Users
    if (action.includes('System')) return Database
    if (action.includes('Login')) return Shield
    if (action.includes('Configuration')) return Settings
    return Activity
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold gradient-text">Audit Management</h1>
              <p className="text-gray-600 mt-2">
                Monitor and track all system activities and changes
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={refreshData}
                disabled={loading}
                className="btn-outline"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button className="btn-gradient">
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Audits</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {mockTransactionAudits.length + mockUserAudits.length + mockSystemAudits.length}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <Eye className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Successful Actions</p>
                  <p className="text-2xl font-bold text-green-600">
                    {
                      [...mockTransactionAudits, ...mockUserAudits, ...mockSystemAudits].filter(
                        a => a.status === 'success'
                      ).length
                    }
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Failed Actions</p>
                  <p className="text-2xl font-bold text-red-600">
                    {
                      [...mockTransactionAudits, ...mockUserAudits, ...mockSystemAudits].filter(
                        a => a.status === 'failed'
                      ).length
                    }
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-full">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-content">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Today's Activity</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {
                      [...mockTransactionAudits, ...mockUserAudits, ...mockSystemAudits].filter(
                        a => {
                          const today = new Date()
                          const auditDate = new Date(a.timestamp)
                          return auditDate.toDateString() === today.toDateString()
                        }
                      ).length
                    }
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <Activity className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="card mb-6">
          <div className="card-content p-0">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                {tabs.map(tab => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-red-500 text-red-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab !== 'reports' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="card">
              <div className="card-content">
                <div className="flex flex-col lg:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search audit logs..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="input pl-10"
                      />
                    </div>
                  </div>

                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={e => handleDateRangeChange('start', e.target.value)}
                    className="input"
                    placeholder="Start Date"
                  />

                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={e => handleDateRangeChange('end', e.target.value)}
                    className="input"
                    placeholder="End Date"
                  />

                  <select className="input">
                    <option value="">All Status</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Audit Logs Table */}
            <div className="card">
              <div className="card-content p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-gray-200">
                      <tr>
                        <th className="text-left py-4 px-6 font-semibold text-gray-900">Action</th>
                        <th className="text-left py-4 px-6 font-semibold text-gray-900">User</th>
                        <th className="text-left py-4 px-6 font-semibold text-gray-900">Changes</th>
                        <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                        <th className="text-left py-4 px-6 font-semibold text-gray-900">
                          Timestamp
                        </th>
                        <th className="text-left py-4 px-6 font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map(audit => {
                        const statusConfig = getStatusConfig(audit.status)
                        const StatusIcon = statusConfig.icon
                        const AuditIcon = getAuditIcon(audit.action)

                        return (
                          <tr key={audit.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-4 px-6">
                              <div className="flex items-center space-x-3">
                                <div className="p-1 bg-gray-50 rounded">
                                  <AuditIcon className="h-4 w-4 text-gray-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{audit.action}</p>
                                  {audit.id && <p className="text-sm text-gray-500">{audit.id}</p>}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div>
                                <p className="text-sm text-gray-900">{audit.user}</p>
                                <p className="text-xs text-gray-500">{audit.ip_address}</p>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="max-w-xs">
                                {audit.old_value && (
                                  <div className="mb-1">
                                    <p className="text-xs text-gray-500">From:</p>
                                    <p className="text-xs text-red-600 bg-red-50 p-1 rounded truncate">
                                      {audit.old_value}
                                    </p>
                                  </div>
                                )}
                                {audit.new_value && (
                                  <div>
                                    <p className="text-xs text-gray-500">To:</p>
                                    <p className="text-xs text-green-600 bg-green-50 p-1 rounded truncate">
                                      {audit.new_value}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span
                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}
                              >
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {audit.status}
                              </span>
                            </td>
                            <td className="py-4 px-6">
                              <div>
                                <p className="text-sm text-gray-900">
                                  {new Date(audit.timestamp).toLocaleDateString()}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatDistanceToNow(new Date(audit.timestamp), {
                                    addSuffix: true,
                                  })}
                                </p>
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex items-center space-x-2">
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Report Generation */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title text-xl">Generate Audit Reports</h3>
                <p className="card-description">
                  Create comprehensive audit reports for compliance and analysis
                </p>
              </div>
              <div className="card-content">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="card border-2 border-dashed border-gray-300 hover:border-red-500 transition-colors cursor-pointer">
                    <div className="card-content text-center py-8">
                      <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="font-semibold text-gray-900 mb-2">Transaction Audit Report</h3>
                      <p className="text-sm text-gray-500 mb-4">Complete transaction audit trail</p>
                      <Button className="btn-outline">Generate Report</Button>
                    </div>
                  </div>

                  <div className="card border-2 border-dashed border-gray-300 hover:border-red-500 transition-colors cursor-pointer">
                    <div className="card-content text-center py-8">
                      <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="font-semibold text-gray-900 mb-2">User Activity Report</h3>
                      <p className="text-sm text-gray-500 mb-4">User actions and access logs</p>
                      <Button className="btn-outline">Generate Report</Button>
                    </div>
                  </div>

                  <div className="card border-2 border-dashed border-gray-300 hover:border-red-500 transition-colors cursor-pointer">
                    <div className="card-content text-center py-8">
                      <Database className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="font-semibold text-gray-900 mb-2">System Changes Report</h3>
                      <p className="text-sm text-gray-500 mb-4">System configuration changes</p>
                      <Button className="btn-outline">Generate Report</Button>
                    </div>
                  </div>

                  <div className="card border-2 border-dashed border-gray-300 hover:border-red-500 transition-colors cursor-pointer">
                    <div className="card-content text-center py-8">
                      <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="font-semibold text-gray-900 mb-2">Security Audit Report</h3>
                      <p className="text-sm text-gray-500 mb-4">Security events and violations</p>
                      <Button className="btn-outline">Generate Report</Button>
                    </div>
                  </div>

                  <div className="card border-2 border-dashed border-gray-300 hover:border-red-500 transition-colors cursor-pointer">
                    <div className="card-content text-center py-8">
                      <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="font-semibold text-gray-900 mb-2">Compliance Report</h3>
                      <p className="text-sm text-gray-500 mb-4">Regulatory compliance summary</p>
                      <Button className="btn-outline">Generate Report</Button>
                    </div>
                  </div>

                  <div className="card border-2 border-dashed border-gray-300 hover:border-red-500 transition-colors cursor-pointer">
                    <div className="card-content text-center py-8">
                      <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <h3 className="font-semibold text-gray-900 mb-2">Custom Report</h3>
                      <p className="text-sm text-gray-500 mb-4">Create custom audit report</p>
                      <Button className="btn-outline">Configure Report</Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Reports */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title text-xl">Recent Reports</h3>
                <p className="card-description">Previously generated audit reports</p>
              </div>
              <div className="card-content">
                <div className="space-y-4">
                  {[
                    {
                      name: 'Transaction Audit - Q4 2024',
                      date: '2024-01-15',
                      size: '2.3 MB',
                      status: 'completed',
                    },
                    {
                      name: 'User Activity - December 2024',
                      date: '2024-01-10',
                      size: '1.8 MB',
                      status: 'completed',
                    },
                    {
                      name: 'Security Audit - Q4 2024',
                      date: '2024-01-05',
                      size: '3.1 MB',
                      status: 'completed',
                    },
                  ].map((report, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{report.name}</p>
                          <p className="text-sm text-gray-500">
                            Generated on {report.date} • {report.size}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-green-600 bg-green-50 border border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {report.status}
                        </span>
                        <Button variant="ghost" size="sm" className="h-8 px-3 text-xs">
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
